import React, { useEffect, useState, useCallback } from 'react';
import styled from 'App/styled';
import { useTheme } from 'emotion-theming';

import Portal from 'shared/components/Portal';
import YesTick from 'components/Icons/YesTick';

const FALLBACK_ID = 'snackbar';
const FALLBACK_ACTIVE_CLASS = 'active';
const DISPLAY_TIME = 2500;
const WAIT_TIME_FOR_CLOSE_ANIMATION = 300; // This is equal to CSS transition animation duration;

interface SnackbarTheme {
  background: string;
}

const Wrapper = styled.div`
  position: fixed;
  z-index: 10;
  bottom: 0;
  left: 0;
  right: 0;
  background: transparent;

  .container {
    height: 100%;
    display: flex;
    align-items: center;
    background: ${({ background }: SnackbarTheme) => background};
    color: #fff;
    font-weight: 600;
    min-height: 55px;
    padding: 0 10px;
    margin-bottom: -80px;
    transition: all 0.3s;

    &.${FALLBACK_ACTIVE_CLASS} {
      margin-bottom: 0;
    }

    .body {
      flex: 1;
      ${({ theme }) => theme.typography.bodyMedium};
    }

    .icon {
      margin-right: 5px;
      svg {
        vertical-align: middle;
      }
    }

    .close-snackbar {
      background: transparent;
      padding: 4px 10px;
      ${({ theme }) => theme.typography.type200};
      border: none;
      color: #fff;

      &:focus {
        outline: none;
      }
    }
  }
`;

interface SnackbarProps {
  content: string | React.ReactNode;
  autoClose: boolean;
  closeCallback: Function;
  errorType?: boolean;
  snackBarId: string;
}

function Snackbar({
  content,
  autoClose,
  closeCallback,
  errorType = false,
  snackBarId = FALLBACK_ID,
}: SnackbarProps) {
  const [isMounted, setIsMounted] = useState(false);
  const theme: Theme = useTheme();

  const handleMount = useCallback(() => {
    if (isMounted) {
      const snackBarNode = document.getElementById(snackBarId);
      if (snackBarNode) {
        snackBarNode.classList.add(FALLBACK_ACTIVE_CLASS);
      }
    }
  }, [isMounted, snackBarId]);

  const handleClose = useCallback(() => {
    if (isMounted) {
      const snackBarNode = document.getElementById(snackBarId);
      if (snackBarNode) {
        snackBarNode.classList.remove(FALLBACK_ACTIVE_CLASS);
        setTimeout(() => {
          closeCallback();
        }, WAIT_TIME_FOR_CLOSE_ANIMATION);
      }
    }
  }, [isMounted, snackBarId, closeCallback]);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  useEffect(() => {
    handleMount();
  }, [handleMount]);

  useEffect(() => {
    if (autoClose) {
      setTimeout(() => {
        handleClose();
      }, DISPLAY_TIME);
    }
  }, [autoClose, handleClose]);

  const background = errorType ? theme.brickColor : theme.brandPrimary;

  return (
    <Portal>
      <Wrapper background={background}>
        <div
          className="container"
          id={snackBarId}
          role="status"
          aria-live="polite"
          aria-atomic="true"
        >
          <div className="body">
            <i
              className="icon"
              aria-hidden="true"
            >
              <YesTick />
            </i>
            {content}
          </div>
          {!autoClose && (
            <button
              type="button"
              className="close-snackbar"
              onClick={handleClose}
              aria-label="Close notification"
            >
              &#10006;
            </button>
          )}
        </div>
      </Wrapper>
    </Portal>
  );
}

export default Snackbar;
